// ─── ViaturasView — valor/hora da viatura (Casais) ───────────────────────
// Lista as viaturas do EcooGPS e permite definir o VALOR/HORA (campo aditivo
// local por deviceId; sobrevive ao sync). Usado no custo dos transportes.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Tooltip, CircularProgress, Chip, Button, InputAdornment,
} from '@material-ui/core';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerDialogTheme, usePlannerToolbarTheme, brandGradient, ON_BRAND } from '../../theme/plannerTheme';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import CheckIcon from '@material-ui/icons/Check';
import { setActiveView } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surf-page, #f8fafc)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', background: 'var(--surf-card, #fff)', borderBottom: '1px solid var(--surf-border, #e2e8f0)' },
  title: { fontSize: '1.05rem', fontWeight: 800, color: 'var(--surf-title, #0f172a)', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', marginTop: 2 },
  toolbar: { display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)', background: 'var(--surf-card, #fff)' },
  searchInput: { flex: 1, maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: 8, background: 'var(--surf-input-bg, #fff)', fontSize: '0.78rem' } },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  headerIcon: { width: 34, height: 34, borderRadius: 10, background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  row: { background: 'var(--surf-card, #fff)', borderRadius: 12, padding: '11px 16px', marginBottom: 9, border: '1px solid var(--surf-border, #e8edf3)', display: 'flex', alignItems: 'center', gap: 14, transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: '#fcd34d', boxShadow: '0 4px 14px rgba(217,119,6,0.08)' } },
  avatar: { width: 38, height: 38, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  plate: { fontSize: '0.88rem', fontWeight: 700, color: 'var(--surf-strong, #0f172a)' },
  meta: { fontSize: '0.68rem', color: 'var(--surf-muted2, #94a3b8)', marginTop: 1 },
  empty: { padding: '80px 24px', textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
}));

function ViaturasView({ lang = 'pt' }) {
  const classes = useStyles();
  const theme = usePlannerTheme();
  const newUI = usePlannerToolbarTheme() !== 'classic'; // identidade ecooGPS só em Claro/Escuro
  const grad = brandGradient(theme);
  const dlgTheme = usePlannerDialogTheme();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [vehicles, setVehicles] = useState([]);
  const [rates, setRates] = useState({});      // deviceId → valor/hora
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [vr, rr] = await Promise.all([
        fetch('/planner-api/vehicles?includeDisabled=true', { headers }).then(r => r.ok ? r.json() : []),
        fetch('/planner-api/vehicles/rates', { headers }).then(r => r.ok ? r.json() : { rates: {} }),
      ]);
      setVehicles(Array.isArray(vr) ? vr : []);
      setRates((rr && rr.rates) || {});
    } catch (e) {
      setError(pt ? 'Não foi possível carregar as viaturas.' : 'Could not load vehicles.');
    } finally { setLoading(false); }
  }, [token, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function saveRate(v) {
    const id = v.id;
    const current = rates[id] != null ? String(rates[id]) : '';
    const value = edits[id] !== undefined ? edits[id] : current;
    if (current === value) return;
    setSavingId(id); setError(null);
    try {
      const r = await fetch(`/planner-api/vehicles/${id}/rate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hourlyRate: value === '' ? null : Number(value) }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const data = await r.json();
      setRates(prev => ({ ...prev, [id]: data.hourlyRate }));
      setEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
      setSavedId(id); setTimeout(() => setSavedId(cur => cur === id ? null : cur), 1500);
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally { setSavingId(null); }
  }

  const filtered = vehicles.filter(v => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [v.plate, v.name, v.model].some(x => x && String(x).toLowerCase().includes(q));
  });

  return (
    <ThemeProvider theme={dlgTheme}>
    <Box className={classes.root} data-planner-theme={theme}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}><ArrowBackIcon style={{ fontSize: 20 }} /></IconButton>
        </Tooltip>
        <Box style={{ flex: 1 }}>
          <Typography className={classes.title}><Box component="span" className={classes.headerIcon} style={newUI ? { background: grad, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' } : undefined}><LocalShippingIcon style={{ fontSize: 19, color: newUI ? ON_BRAND : '#d97706' }} /></Box> {pt ? 'Viaturas' : 'Vehicles'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Viaturas do EcooGPS — definir valor/hora (custo dos transportes)' : 'EcooGPS vehicles — set hourly rate'}</Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}><IconButton size="small" onClick={fetchAll} disabled={loading}><RefreshIcon style={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField className={classes.searchInput} size="small" variant="outlined"
          placeholder={pt ? 'Procurar matrícula…' : 'Search plate…'} value={search} onChange={e => setSearch(e.target.value)} />
        <Box style={{ flex: 1 }} />
        <Chip label={`${vehicles.length} ${pt ? 'viaturas' : 'vehicles'}`} size="small" style={{ background: '#fef3c7', color: '#d97706', fontWeight: 700 }} />
      </Box>

      <Box className={classes.list}>
        {error && <Box style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem' }}>{error}</Box>}
        {loading && vehicles.length === 0 ? (
          <Box className={classes.empty}><CircularProgress size={32} style={{ color: '#d97706' }} /></Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            {newUI
              ? <Box style={{ width: 66, height: 66, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf-head-bg, #f1f6f9)', color: theme === 'dark' ? '#6f93a6' : '#94a3b8' }}><LocalShippingIcon style={{ fontSize: 32 }} /></Box>
              : <LocalShippingIcon style={{ fontSize: 48, color: '#cbd5e1' }} />}
            <Typography style={{ fontWeight: 700, color: 'var(--surf-title, #0f172a)' }}>{pt ? 'Sem viaturas.' : 'No vehicles.'}</Typography>
          </Box>
        ) : filtered.map(v => {
          const id = v.id;
          const current = rates[id] != null ? String(rates[id]) : '';
          const val = edits[id] !== undefined ? edits[id] : current;
          const dirty = current !== val;
          return (
            <Box key={id} className={classes.row}>
              <Box className={classes.avatar}><LocalShippingIcon style={{ fontSize: 18, color: '#d97706' }} /></Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.plate}>{v.plate || v.name || `#${id}`}</Typography>
                <Typography className={classes.meta}>{[v.model, v.type].filter(Boolean).join(' · ') || (pt ? 'Viatura EcooGPS' : 'EcooGPS vehicle')}</Typography>
              </Box>
              <TextField
                size="small" variant="outlined" type="number"
                label={pt ? 'Valor/hora' : 'Hourly rate'}
                value={val}
                onChange={e => setEdits(prev => ({ ...prev, [id]: e.target.value }))}
                onBlur={() => saveRate(v)}
                onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
                InputProps={{ endAdornment: <InputAdornment position="end">€/h</InputAdornment> }}
                inputProps={{ min: 0, step: 0.5 }}
                style={{ width: 170 }}
              />
              {savingId === id ? <CircularProgress size={18} />
                : savedId === id ? <CheckIcon style={{ fontSize: 20, color: '#16a34a' }} />
                : dirty ? (
                  <Button size="small" onClick={() => saveRate(v)} style={{ textTransform: 'none', fontSize: '0.72rem', color: '#d97706' }}>{pt ? 'Guardar' : 'Save'}</Button>
                ) : <Box style={{ width: 24 }} />}
            </Box>
          );
        })}
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default ViaturasView;
